import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
  noindex?: boolean;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

const SEO: React.FC<SEOProps> = ({
  title = 'GlobalPreps - Professional Certification Exam Practice Platform',
  description = 'Master your certification exams with GlobalPreps. Practice AWS, PMP, and 100+ professional certifications with instant feedback, exam simulations, and detailed analytics. Free tier available.',
  keywords = 'certification exams, AWS certification, PMP exam, professional certification, exam practice, test preparation, certification training, exam simulator',
  image = '/og-image.png',
  url,
  type = 'website',
  structuredData,
  noindex = false,
  breadcrumbs
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  
  const fullTitle = title.includes('GlobalPreps') ? title : `${title} | GlobalPreps`;
  const baseUrl = 'https://globalpreps.com';
  const langPrefix = currentLang === 'en' ? '' : `/${currentLang}`;
  const fullUrl = url 
    ? (url.startsWith('http') ? url : `${baseUrl}${url}`)
    : `${baseUrl}${langPrefix}${location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/'}`;
  const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  // Supported languages for hreflang
  const supportedLanguages = [
    { code: 'en', url: 'en_US' },
    { code: 'zh-CN', url: 'zh_CN' },
    { code: 'zh-TW', url: 'zh_TW' },
    { code: 'fr', url: 'fr_FR' },
    { code: 'de', url: 'de_DE' },
    { code: 'id', url: 'id_ID' },
    { code: 'it', url: 'it_IT' },
    { code: 'ja', url: 'ja_JP' },
    { code: 'ko', url: 'ko_KR' },
    { code: 'pt-BR', url: 'pt_BR' },
    { code: 'es-ES', url: 'es_ES' },
    { code: 'es-MX', url: 'es_MX' },
    { code: 'ar', url: 'ar_SA' },
  ];

  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update HTML lang attribute
    document.documentElement.lang = currentLang;

    // Helper function to update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang 
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let element = document.querySelector(selector) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        if (hreflang) element.setAttribute('hreflang', hreflang);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Remove old hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    // Primary Meta Tags
    updateMetaTag('title', fullTitle);
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Robots meta tag
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Open Graph Tags
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:url', fullUrl, 'property');
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', fullImage, 'property');
    updateMetaTag('og:locale', supportedLanguages.find(l => l.code === currentLang)?.url || 'en_US', 'property');
    
    // Add hreflang alternatives for Open Graph
    supportedLanguages.forEach(lang => {
      const langPrefix = lang.code === 'en' ? '' : `/${lang.code}`;
      const altUrl = url 
        ? (url.startsWith('http') ? url : `${baseUrl}${url}`)
        : `${baseUrl}${langPrefix}${location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/'}`;
      updateMetaTag(`og:locale:alternate`, lang.url, 'property');
    });

    // Twitter Tags
    updateMetaTag('twitter:card', 'summary_large_image', 'property');
    updateMetaTag('twitter:url', fullUrl, 'property');
    updateMetaTag('twitter:title', fullTitle, 'property');
    updateMetaTag('twitter:description', description, 'property');
    updateMetaTag('twitter:image', fullImage, 'property');

    // Canonical URL
    updateLinkTag('canonical', fullUrl);

    // Hreflang tags for international SEO
    supportedLanguages.forEach(lang => {
      const langPrefix = lang.code === 'en' ? '' : `/${lang.code}`;
      const altUrl = url 
        ? (url.startsWith('http') ? url : `${baseUrl}${url}`)
        : `${baseUrl}${langPrefix}${location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/'}`;
      
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang.code);
      link.setAttribute('href', altUrl);
      document.head.appendChild(link);
    });
    
    // Add x-default hreflang (points to English version)
    const defaultUrl = url 
      ? (url.startsWith('http') ? url : `${baseUrl}${url}`)
      : `${baseUrl}${location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?/, '') || '/'}`;
    updateLinkTag('alternate', defaultUrl, 'x-default');

    // Structured Data
    let structuredDataToUse = structuredData;
    
    // Add breadcrumbs structured data if provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url.startsWith('http') ? crumb.url : `${baseUrl}${crumb.url}`
        }))
      };
      
      if (structuredDataToUse) {
        // If custom structured data exists, add breadcrumbs to it
        structuredDataToUse = Array.isArray(structuredDataToUse) 
          ? [...structuredDataToUse, breadcrumbSchema]
          : [structuredDataToUse, breadcrumbSchema];
      } else {
        structuredDataToUse = breadcrumbSchema;
      }
    }
    
    if (structuredDataToUse) {
      // Remove old structured data scripts
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => {
        // Keep the initial one from index.html if it exists
        const existingScript = el.textContent;
        if (
          existingScript &&
          (existingScript.includes('EducationalOrganization') || existingScript.includes('"@graph"'))
        ) {
          return; // Keep the main site schema
        }
        el.remove();
      });
      
      const schemas = Array.isArray(structuredDataToUse) ? structuredDataToUse : [structuredDataToUse];
      schemas.forEach(schema => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [fullTitle, description, keywords, fullImage, fullUrl, type, structuredData, location.pathname, currentLang, noindex, breadcrumbs]);

  return null; // This component doesn't render anything
};

export default SEO;
